/**
 * Parse a typed or pasted money amount or quantity in the project's number format.
 * Accepts plain numbers plus common formatting (grouping, currency symbols, spaces).
 * Returns null for empty / incomplete / invalid input.
 *
 * `locale` is the project's number format (`projectNumberLocale()`), for typed and pasted amounts
 * alike: "1,500" in an en-GB project and "1.500" in a de-DE project both read as fifteen hundred,
 * so tabbing through a field never divides it by 1000.
 */
export function parseMoneyInput(
  raw: string,
  options: { locale: string; allowNegative?: boolean }
): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const group = groupingCharFor(options.locale);
  // Drop currency letters and symbols. Spaces and apostrophes only ever group digits
  // ("1 500,00", "1'500.00"), so they go too.
  let cleaned = trimmed.replace(/[^\d.,-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === ',') return null;

  const hasDot = cleaned.includes('.');
  const hasComma = cleaned.includes(',');
  if (hasDot && hasComma) {
    // Both separators: the last one is the decimal.
    const decimalChar = cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.') ? ',' : '.';
    const groupChar = decimalChar === ',' ? '.' : ',';
    cleaned = cleaned.split(groupChar).join('').replace(decimalChar, '.');
  } else if (hasDot || hasComma) {
    const sep = hasDot ? '.' : ',';
    const parts = cleaned.split(sep);
    const isOnce = parts.length === 2;
    // Valid grouping: 1 to 3 digits not starting with 0, then groups of exactly 3
    // ("1,500", "12,345,678"; not "0,125", which is a fraction).
    const isGrouping =
      /^-?[1-9]\d{0,2}$/.test(parts[0]) && parts.slice(1).every((p) => /^\d{3}$/.test(p));
    let readAsDecimal: boolean;
    if (sep === group) {
      // The project's grouping character: grouping when it fits ("1,500" in en-GB), else a
      // decimal typed the other way ("12,50").
      readAsDecimal = !isGrouping && isOnce;
    } else {
      // The project's decimal character ("1,5" in en-ZA), or one it uses for neither ("1.234" in
      // en-ZA, which groups with spaces): a decimal when it appears once.
      readAsDecimal = isOnce;
    }
    if (readAsDecimal) {
      cleaned = parts.join('.');
    } else if (isGrouping) {
      // Repeated, with valid groups ("1,234,567"): can only be grouping.
      cleaned = parts.join('');
    } else {
      return null;
    }
  }

  return finish(cleaned, options);
}

/** A number with "." as its only decimal point, or null while incomplete or invalid. */
function finish(cleaned: string, options?: { allowNegative?: boolean }): number | null {
  // Incomplete while typing (e.g. "12." or "-") — not yet a committed value.
  if (cleaned.endsWith('.') || cleaned === '-' || cleaned === '-.') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  if (n < 0 && !options?.allowNegative) return null;
  return n;
}


/** The character a locale groups thousands with ("," when the locale is unknown). */
function groupingCharFor(locale: string): string {
  try {
    // Space-like groups (en-ZA, fr-FR) were stripped with the other spaces, so they never match.
    return new Intl.NumberFormat(locale).formatToParts(12345.6).find((p) => p.type === 'group')?.value ?? ',';
  } catch {
    return ',';
  }
}
